import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import FileUpload, { getFileUrl } from "@/components/FileUpload";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

const SECTIONS = ["hero", "about", "label", "contact"];

export default function HomepageManager() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [active, setActive] = useState("hero");

  useEffect(() => {
    api.get("/homepage").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateField = (section, key, value) => {
    setData(d => ({ ...d, [section]: { ...d[section], [key]: value } }));
  };

  const updateArrayField = (section, key, index, value) => {
    setData(d => {
      const arr = [...(d[section]?.[key] || [])];
      arr[index] = value;
      return { ...d, [section]: { ...d[section], [key]: arr } };
    });
  };

  const saveSection = async (section) => {
    setSaving(s => ({ ...s, [section]: true }));
    try {
      await api.put(`/homepage/${section}`, { data: data[section] });
      toast.success(`${section} section saved`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(s => ({ ...s, [section]: false })); }
  };

  if (loading) return <p className="text-white/30">Loading...</p>;

  const hero = data.hero || {};
  const about = data.about || {};
  const label = data.label || {};
  const contact = data.contact || {};

  return (
    <div data-testid="admin-homepage-manager">
      <h1 className="font-heading text-3xl mb-1">HOMEPAGE</h1>
      <p className="text-white/40 text-sm mb-6">Edit your homepage sections</p>

      {/* Section tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {SECTIONS.map(s => (
          <button key={s} onClick={() => setActive(s)} className={`px-4 py-2 rounded text-xs uppercase tracking-wider border transition-colors ${active === s ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-white/40 hover:text-white"}`}>{s}</button>
        ))}
      </div>

      {/* Hero */}
      {active === "hero" && (
        <div className="bg-[#111111] border border-white/5 p-6 rounded space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Title</label>
            <input value={hero.title || ""} onChange={e => updateField("hero", "title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Tagline</label>
            <input value={hero.tagline || ""} onChange={e => updateField("hero", "tagline", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Subtext</label>
            <input value={hero.subtext || ""} onChange={e => updateField("hero", "subtext", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Label Text</label>
            <input value={hero.label_text || ""} onChange={e => updateField("hero", "label_text", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Background Image URL (or upload)</label>
            <input value={hero.background_image || ""} onChange={e => updateField("hero", "background_image", e.target.value)} placeholder="https://... or upload below" className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20 mb-2" />
            <FileUpload accept="image/*" value="" onChange={v => updateField("hero", "background_image", v)} />
          </div>
          {hero.background_image && <img src={getFileUrl(hero.background_image)} alt="Hero preview" className="h-32 object-cover rounded border border-white/10" />}
          <Button data-testid="save-hero-btn" onClick={() => saveSection("hero")} disabled={saving.hero} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
            {saving.hero ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Hero
          </Button>
        </div>
      )}

      {/* About */}
      {active === "about" && (
        <div className="bg-[#111111] border border-white/5 p-6 rounded space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Section Title</label>
            <input value={about.title || ""} onChange={e => updateField("about", "title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          {(about.paragraphs || []).map((p, i) => (
            <div key={i}>
              <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Paragraph {i + 1}</label>
              <textarea value={p} onChange={e => updateArrayField("about", "paragraphs", i, e.target.value)} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] resize-none" />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Quote</label>
            <input value={about.quote || ""} onChange={e => updateField("about", "quote", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <Button data-testid="save-about-btn" onClick={() => saveSection("about")} disabled={saving.about} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
            {saving.about ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save About
          </Button>
        </div>
      )}

      {/* Label */}
      {active === "label" && (
        <div className="bg-[#111111] border border-white/5 p-6 rounded space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Title</label>
            <input value={label.title || ""} onChange={e => updateField("label", "title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Description</label>
            <textarea value={label.description || ""} onChange={e => updateField("label", "description", e.target.value)} rows={4} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] resize-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Quote</label>
            <input value={label.quote || ""} onChange={e => updateField("label", "quote", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <Button data-testid="save-label-btn" onClick={() => saveSection("label")} disabled={saving.label} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
            {saving.label ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Label
          </Button>
        </div>
      )}

      {/* Contact */}
      {active === "contact" && (
        <div className="bg-[#111111] border border-white/5 p-6 rounded space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-1 block">Business Email</label>
            <input value={contact.email || ""} onChange={e => updateField("contact", "email", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37]" />
          </div>
          <p className="text-xs uppercase tracking-wider text-white/40">Social Links</p>
          {(contact.social_links || []).map((link, i) => (
            <div key={i} className="grid grid-cols-3 gap-2">
              <input value={link.platform} disabled className="bg-[#0A0A0A] border border-white/5 p-2.5 rounded text-sm text-white/40" />
              <input value={link.handle || ""} onChange={e => { const links = [...(contact.social_links || [])]; links[i] = { ...links[i], handle: e.target.value }; updateField("contact", "social_links", links); }} placeholder="Handle" className="bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20" />
              <input value={link.url || ""} onChange={e => { const links = [...(contact.social_links || [])]; links[i] = { ...links[i], url: e.target.value }; updateField("contact", "social_links", links); }} placeholder="URL" className="bg-[#0A0A0A] border border-white/10 p-2.5 rounded text-sm text-white outline-none focus:border-[#D4AF37] placeholder:text-white/20" />
            </div>
          ))}
          <Button data-testid="save-contact-btn" onClick={() => saveSection("contact")} disabled={saving.contact} className="bg-[#D4AF37] text-black hover:bg-white text-sm">
            {saving.contact ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Contact
          </Button>
        </div>
      )}
    </div>
  );
}
